import type { APIClient } from "@heidelbergmaterials/fcl-apiclient"
import { Avatar, Gavel, MenuItemConfig, icon } from "@hm/fundamental"
import { PermissionType } from "@shared/api/authentication/types"
import { formatLandscapePrefix } from "@shared/lib"
import { useAuth } from "@shared/lib/auth"
import {
	imprint,
	openSourceAttribution,
	privacyPolicy,
	termsAndConditions,
} from "@shared/routes"
import { useFilterStore } from "@shared/stores"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { LanguageOption } from "./types"

interface UseUserMenuItemsParams {
	apiClient: APIClient
	availableLanguages: LanguageOption[]
	selectedLocale: string | undefined
	handleLanguageChanged: (languageCode: string) => Promise<void>
	notificationSettingsView?: React.ReactNode
}

interface UseUserMenuItemsResult {
	loggedInItems: MenuItemConfig[]
	loggedOutItems: MenuItemConfig[]
	isAuthenticated: boolean
}

export const useUserMenuItems = ({
	apiClient,
	availableLanguages,
	selectedLocale,
	handleLanguageChanged,
	notificationSettingsView,
}: UseUserMenuItemsParams): UseUserMenuItemsResult => {
	const navigate = useNavigate()
	const { t } = useTranslation("features", { keyPrefix: "userMenu" })

	const {
		isAuthenticated,
		userPermissions,
		decodedAccessToken,
		redirectToAuthenticator,
		startLogoutFlow,
	} = useAuth()

	const [customers, fetchCustomerFilterData] = useFilterStore(store => [
		store.customers,
		store.fetchCustomerFilterData,
	])

	const isCustomer = useMemo(
		() =>
			userPermissions
				?.map(x => x.permissionType)
				.includes(PermissionType.Customer) ?? false,
		[userPermissions],
	)

	const canViewCustomerAccounts = useMemo(
		() =>
			userPermissions?.some(
				x =>
					x.permissionType === PermissionType.Customer ||
					x.permissionType === PermissionType.SalesRepresentative,
			) ?? false,
		[userPermissions],
	)

	useEffect(() => {
		if (isAuthenticated && canViewCustomerAccounts) {
			fetchCustomerFilterData(apiClient)
		}
	}, [
		isAuthenticated,
		canViewCustomerAccounts,
		apiClient,
		fetchCustomerFilterData,
	])

	const carbonBankCustomerItems: MenuItemConfig[] = useMemo(() => {
		if (!customers) return []

		const formattedUserAccounts = customers.map(
			x =>
				`${formatLandscapePrefix(x.customerNumber)} - ${x.customerName}`,
		)

		return [
			{
				key: "accounts",
				title: t("accounts"),
				icon: icon.mdiAccountCircleOutline,
				subtitle: formattedUserAccounts,
				subtitleClassname:
					"whitespace-nowrap overflow-hidden text-ellipsis",
			},
		]
	}, [customers, t])

	const aboutItems: MenuItemConfig[] = useMemo(
		() => [
			{
				key: "terms_and_conditions",
				title: t("about.termsAndConditions"),
				customIcon: <Gavel />,
				onClicked: () => navigate(termsAndConditions),
			},
			{
				key: "privacy_policy",
				title: t("about.privacyPolicy"),
				icon: icon.mdiShieldAlertOutline,
				onClicked: () => navigate(privacyPolicy),
			},
			{
				key: "open_source",
				title: t("about.openSourceAttribution"),
				icon: icon.mdiClipboardTextOutline,
				onClicked: () => navigate(openSourceAttribution),
			},
			{
				key: "imprint",
				title: t("about.imprint"),
				icon: icon.mdiCopyright,
				onClicked: () => navigate(imprint),
			},
		],
		[t, navigate],
	)

	const commonItems: MenuItemConfig[] = useMemo(
		() => [
			{
				key: "about",
				title: t("about.title"),
				icon: icon.mdiInformationOutline,
				items: aboutItems,
			},
			{
				key: "language",
				title: t("language.title"),
				footer: t("language.footer"),
				icon: icon.mdiTranslate,
				type: "select",
				onClicked: handleLanguageChanged,
				selectedItem: selectedLocale,
				items: availableLanguages,
			},
		],
		[
			aboutItems,
			availableLanguages,
			handleLanguageChanged,
			selectedLocale,
			t,
		],
	)

	const loggedInItems: MenuItemConfig[] = useMemo(
		() => [
			{
				key: "user",
				title: decodedAccessToken?.name ?? "",
				customIcon: <Avatar name={decodedAccessToken?.name ?? ""} />,
				subtitle: decodedAccessToken?.email ?? "",
			},
			...carbonBankCustomerItems,
			{
				key: "subscribe",
				title: t("subscribeToFeedback"),
				icon: icon.mdiEmailMarkAsUnread,
			},
			...(isCustomer
				? [
						{
							key: "notification_settings",
							title: t("notificationSettings.title"),
							icon: icon.mdiBellCogOutline,
							view: notificationSettingsView,
						},
					]
				: []),
			...commonItems,
			{
				key: "logout",
				title: t("logout"),
				icon: icon.mdiLogout,
				iconClasses: "text-red-400",
				onClicked: startLogoutFlow,
			},
		],
		[
			carbonBankCustomerItems,
			commonItems,
			decodedAccessToken,
			isCustomer,
			notificationSettingsView,
			t,
			startLogoutFlow,
		],
	)

	const loggedOutItems: MenuItemConfig[] = useMemo(
		() => [
			...commonItems,
			{
				key: "login",
				title: t("login"),
				icon: icon.mdiLogin,
				iconClasses: "text-red-400",
				onClicked: redirectToAuthenticator,
			},
		],
		[commonItems, t, redirectToAuthenticator],
	)

	return {
		loggedInItems,
		loggedOutItems,
		isAuthenticated,
	}
}
